import { createReducer } from '@reduxjs/toolkit';

import { FocusAreasManager, Loadable } from 'models';
import { makeThunkFromAPICall } from 'state';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';

export type FocusAreasState = {
    selection: Loadable<FocusAreasManager.ObjectivesStrategiesSelection>;
    list: Loadable<FocusAreasManager.FocusArea[]>;
    listFromGeoserver: Loadable<FocusAreasManager.FocusArea[]>;
    audit: Loadable<FocusAreasManager.FocusAreaAuditRecord[]>;
    deleteFocusAreaResponse: Loadable<FocusAreasManager.API.ActionResponse>;
    editFocusAreaResponse: Loadable<FocusAreasManager.FocusArea>;
    selectedFeature: Loadable<Feature<Geometry>>;
    focusAreaUploads: Loadable<FocusAreasManager.DataUpload[]>;
    editFeature: Loadable<FocusAreasManager.EditFocusArea>;
};

const initialState: FocusAreasState = {
    selection: {
        status: 'idle',
    },
    list: {
        status: 'idle',
    },
    audit: {
        status: 'idle',
    },
    listFromGeoserver: {
        status: 'idle',
    },
    deleteFocusAreaResponse: {
        status: 'idle',
    },
    editFocusAreaResponse: {
        status: 'idle',
    },
    selectedFeature: {
        status: 'idle',
    },
    focusAreaUploads: {
        status: 'idle',
    },
    editFeature: {
        status: 'idle',
    },
};

export const [getObjectivesAndStrategies, setObjectivesAndStrategies] =
    makeThunkFromAPICall(
        FocusAreasManager.getObjectivesAndStrategies,
        'focusareas/treatments/selection',
    );

export const [getFocusAreas, setFocusAreas] = makeThunkFromAPICall(
    FocusAreasManager.getFocusAreas,
    'focusareas/list',
);

export const [getFocusAreaAudit, setFocusAreaAudit] = makeThunkFromAPICall(
    FocusAreasManager.getFocusAreaAudit,
    'focusareas/audit',
);

export const [getFocusAreasFromGeoserver, setFocusAreasFromGeoserver] =
    makeThunkFromAPICall(
        FocusAreasManager.getFocusAreasFromGeoserver,
        'focusareas/geoserver/list',
    );

export const [sendDeleteFocusArea, setDeleteFocusAreaResponse] =
    makeThunkFromAPICall(
        FocusAreasManager.deleteFocusArea,
        'focusareas/delete',
    );
export const [editFocusArea, setEditFocusArea] = makeThunkFromAPICall(
    FocusAreasManager.editFocusArea,
    'focusareas/edit',
);

export const [addFocusArea, setAddFocusArea] = makeThunkFromAPICall(
    FocusAreasManager.addFocusArea,
    'focusareas/add',
);

export const [
    getSelectedFeatureFromGeoserver,
    setSelectedFeatureFromGeoserver,
] = makeThunkFromAPICall(
    FocusAreasManager.getSelectedFeatureFromGeoserver,
    'focusareas/selected/feature',
);

export const [uploadFocusArea] = makeThunkFromAPICall(
    FocusAreasManager.uploadFocusArea,
    'focusareas/data-upload/focusarea',
);

export const [getFocusAreaUploads, setFocusAreaUploads] = makeThunkFromAPICall(
    FocusAreasManager.getFocusAreaUploads,
    'focusareas/data-upload/focusareauploads',
);

export const [editFocusAreaDialog, setEditFocusAreaDialog] =
    makeThunkFromAPICall(
        FocusAreasManager.editFocusAreaDialog,
        'focusareas/edit/feature',
    );

export const focusareasReducer = createReducer(initialState, (builder) => {
    builder.addCase(setObjectivesAndStrategies, (state, action) => {
        return {
            ...state,
            selection: { ...state.selection, ...action.payload.loadable },
        };
    });

    builder.addCase(setFocusAreas, (state, action) => {
        return {
            ...state,
            list: { ...state.list, ...action.payload.loadable },
        };
    });

    builder.addCase(setFocusAreaAudit, (state, action) => {
        return {
            ...state,
            audit: { ...state.audit, ...action.payload.loadable },
        };
    });

    builder.addCase(setFocusAreasFromGeoserver, (state, action) => {
        return {
            ...state,
            listFromGeoserver: {
                ...state.listFromGeoserver,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setDeleteFocusAreaResponse, (state, action) => {
        return {
            ...state,
            deleteFocusAreaResponse: {
                ...state.deleteFocusAreaResponse,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setSelectedFeatureFromGeoserver, (state, action) => {
        return {
            ...state,
            selectedFeature: {
                ...state.selectedFeature,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setFocusAreaUploads, (state, action) => {
        return {
            ...state,
            focusAreaUploads: {
                ...state.focusAreaUploads,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setEditFocusAreaDialog, (state, action) => {
        return {
            ...state,
            editFeature: {
                ...state.editFeature,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setEditFocusArea, (state, action) => {
        return {
            ...state,
            editFocusAreaResponse: {
                ...state.editFocusAreaResponse,
                ...action.payload.loadable,
            },
        };
    });
});

export const FocusAreasActions = {
    getObjectivesAndStrategies,
    sendDeleteFocusArea,
    getFocusAreas,
    getFocusAreaAudit,
    getFocusAreasFromGeoserver,
    getSelectedFeatureFromGeoserver,
    getFocusAreaUploads,
    uploadFocusArea,
    editFocusArea,
    editFocusAreaDialog,
    addFocusArea,
};

export default focusareasReducer;
